import * as THREE from "three";
import { HqMaterials } from "../../hqMaterials";
import { TOWER_KITS } from "../kits";
import { towerOfModel } from "./hide";
import type { ModelSurface } from "./types";

/**
 * A big kit's own tower models (`tower_<id>_t<n>__<model>`) wear the kit's `surface` (types.ts ModelSurface): set once
 * per material as the asset library converts it (HqMaterials.convertHooks), so the level, the hub, the turntable and
 * the shop thumbnails all draw the same glass.
 *
 * The kit's attachment GLB (tier IV) takes it too: its palette glass is baked at roughness 0.06.
 *
 * Why at load and not in the bake: the baked roughness is the look-dev render's, under an area sky; the game lights a
 * tower with a directional key and fill, and on a flat glossy plate that is one highlight from edge to edge (Voidglass's
 * exploded roof plates read as flat beige and pale blue sheets). Raising the floor and putting the light on the edges as
 * a fresnel glint keeps the glass dark with a lit rim.
 */
const SKIN_MODEL = /__([a-z0-9_]+)$/;
const SKIN_GLOW = /_skin_glow$/;
/** The glint's default fresnel exponent: a thin rim on edges and grazing faces. */
const GLINT_POWER = 3;
const ALL_TOWERS = "*";

type SurfacedKit = { readonly asset: string | null; readonly model?: string; readonly surface?: ModelSurface };

/**
 * The `surface` of the kit `modelName` belongs to — one of its own tower models, or its attachment GLB (the tier-IV
 * dressing is the same glass) — or null (stock models, kits without one).
 */
export function modelSurfaceOf(modelName: string, kits: Readonly<Record<string, SurfacedKit>> = TOWER_KITS): ModelSurface | null {
  const skin = /^tower_/.test(modelName) ? SKIN_MODEL.exec(modelName)?.[1] : undefined;
  for (const kit of Object.values(kits)) {
    if (kit.surface && ((skin !== undefined && kit.model === skin) || kit.asset === modelName)) return kit.surface;
  }
  return null;
}

/** The `_skin_glow` multiplier for one tower model (its tower type's entry, else `*`), or null. */
export function skinGlowGain(surface: ModelSurface, modelName: string): readonly [number, number, number] | null {
  const tower = towerOfModel(modelName);
  return (tower ? surface.skinGlow?.[tower] : undefined) ?? surface.skinGlow?.[ALL_TOWERS] ?? null;
}

/** The fragment-shader lines a surface adds, after three's roughness map and after the lighting extras' rim. */
export function surfaceGlsl(s: ModelSurface): { roughness: string; glint: string } {
  return {
    roughness: s.roughnessFloor !== undefined ? "\n  roughnessFactor = max( roughnessFactor, uSurfRoughFloor );" : "",
    glint: s.glint
      ? "\n  totalEmissiveRadiance += uSurfGlint * pow( max( 1e-6, 1.0 - saturate( dot( normal, geometryViewDir ) ) ), uSurfGlintPow );"
      : "",
  };
}

function dressLit(mat: THREE.MeshStandardMaterial, s: ModelSurface): void {
  const glsl = surfaceGlsl(s);
  if (!glsl.roughness && !glsl.glint) return;
  const floor = { value: s.roughnessFloor ?? 0 };
  const glint = { value: new THREE.Color(s.glint?.colour ?? 0).multiplyScalar(s.glint?.strength ?? 0) };
  const power = { value: s.glint?.power ?? GLINT_POWER };
  const prev = mat.onBeforeCompile;
  mat.onBeforeCompile = (sh, r) => {
    prev?.call(mat, sh, r);
    sh.uniforms.uSurfRoughFloor = floor;
    sh.uniforms.uSurfGlint = glint;
    sh.uniforms.uSurfGlintPow = power;
    sh.fragmentShader = sh.fragmentShader
      .replace("#include <common>", "#include <common>\nuniform float uSurfRoughFloor;\nuniform vec3 uSurfGlint;\nuniform float uSurfGlintPow;")
      .replace("#include <roughnessmap_fragment>", `#include <roughnessmap_fragment>${glsl.roughness}`)
      .replace("#include <aomap_fragment>", `#include <aomap_fragment>${glsl.glint}`);
  };
  const old = mat.customProgramCacheKey?.() ?? "";
  mat.customProgramCacheKey = () => `${old}-surf${glsl.roughness ? "r" : ""}${glsl.glint ? "g" : ""}`;
  mat.needsUpdate = true;
}

HqMaterials.convertHooks.push((mat, modelName, glow) => {
  const s = modelSurfaceOf(modelName);
  if (!s) return;
  if (glow) {
    const gain = SKIN_GLOW.test(mat.name) ? skinGlowGain(s, modelName) : null;
    if (gain) (mat as THREE.MeshBasicMaterial).color.multiply(new THREE.Color(gain[0], gain[1], gain[2]));
  } else if ((mat as THREE.MeshStandardMaterial).isMeshStandardMaterial) {
    dressLit(mat as THREE.MeshStandardMaterial, s);
  }
});
